import { type CallInfo, type ClientState, callDuration, isMissedFor } from "@rhema/client";
import {
  ArrowDownLeftIcon,
  ArrowUpRightIcon,
  Avatar,
  cn,
  EmptyState,
  FilterTabs,
  focusRing,
  IconButton,
  ListRow,
  PageHeader,
  PhoneIcon,
  UiLink,
  UsersIcon,
  VideoIcon,
} from "@rhema/ui";
import { type ReactNode, useEffect, useState } from "react";
import { useCalls } from "../calls/CallProvider";
import { callTitle, isGroupCall } from "../calls/people";
import { firstName, listTime, toneFor } from "../data/view";
import { useClientState } from "../session/session";

type Direction = "outgoing" | "incoming" | "missed";

function directionOf(call: CallInfo, meId: string | undefined): Direction {
  if (call.started_by === meId) return "outgoing";
  return isMissedFor(call, meId) ? "missed" : "incoming";
}

function DirectionIcon({ direction }: { direction: Direction }) {
  if (direction === "outgoing") {
    return <ArrowUpRightIcon size={14} strokeWidth={2.4} className="flex-none text-ink-secondary" />;
  }
  return (
    <ArrowDownLeftIcon
      size={14}
      strokeWidth={2.4}
      className={cn("flex-none", direction === "missed" ? "text-scarlet" : "text-olive")}
    />
  );
}

export function durationText(seconds: number): string {
  if (seconds < 60) return `${seconds} sec`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} hr ${m} min` : `${h} hr`;
}

function detailOf(call: CallInfo, direction: Direction, meId: string | undefined): string {
  const media = call.media === "video" ? "video call" : "voice call";
  const when = listTime(call.started_at);
  // Silence unknown callers: the call never rang on this device.
  if (call.participants.some((p) => p.user_id === meId && p.state === "silenced")) {
    return `Silenced ${media} · ${when}`;
  }
  if (!call.ended_at) return `Ongoing ${media}`;
  if (direction === "missed") return `Missed ${media} · ${when}`;
  const mine = call.participants.find((p) => p.user_id === meId);
  if (mine?.state === "declined") return `Declined ${media} · ${when}`;
  const seconds = callDuration(call);
  if (seconds === null) return `${direction === "outgoing" ? "No answer" : "Not answered"} · ${when}`;
  const label = direction === "outgoing" ? "Outgoing" : "Incoming";
  return `${label} ${media} · ${durationText(seconds)} · ${when}`;
}

function ShortcutTile({
  href,
  primary,
  icon,
  children,
}: {
  href: string;
  primary?: boolean;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <UiLink
      href={href}
      className={cn(
        "flex h-[86px] cursor-pointer flex-col items-start justify-center gap-2 rounded-card px-3.5 text-sm font-semibold no-underline",
        primary ? "bg-blue text-white" : "border border-line bg-surface text-ink",
        focusRing,
      )}
    >
      {icon}
      {children}
    </UiLink>
  );
}

function callBackLabel(state: ClientState, call: CallInfo, direction: Direction): string {
  const group = isGroupCall(state, call.chat_id);
  const title = callTitle(state, call);
  const name = group ? title : firstName(title);
  if (call.media === "video") return `Video call ${name}`;
  return direction === "missed" ? `Call ${name} back` : `Call ${name}`;
}

type Filter = "all" | "missed";

export function CallsList({ variant }: { variant: "phone" | "desktop" }) {
  const state = useClientState();
  const { state: calls, markSeen } = useCalls();
  const [filter, setFilter] = useState<Filter>("all");
  const meId = state.me?.id;

  // Opening the list counts any missed calls as seen, which clears the badge.
  useEffect(() => {
    markSeen();
  }, [markSeen]);

  const entries = calls.history
    .filter((c) => state.chats[c.chat_id])
    .map((call) => ({ call, direction: directionOf(call, meId) }))
    .filter((e) => filter === "all" || e.direction === "missed");

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageHeader title="Calls" size={variant === "desktop" ? "md" : "lg"} />
      <div className="grid grid-cols-2 gap-2 px-5">
        <ShortcutTile href="/contacts" primary icon={<PhoneIcon size={20} />}>
          New call
        </ShortcutTile>
        <ShortcutTile href="/chats?filter=groups" icon={<UsersIcon size={20} />}>
          Group call
        </ShortcutTile>
      </div>
      <FilterTabs
        label="Filter calls"
        size="sm"
        value={filter}
        onChange={setFilter}
        options={[
          { id: "all", label: "All" },
          { id: "missed", label: "Missed" },
        ]}
        className="px-5 pt-4"
      />
      <h2 className="m-0 px-5 pt-[18px] pb-1.5 text-[13px] font-bold tracking-[0.04em] text-ink-secondary uppercase">
        Recent
      </h2>
      {entries.length === 0 ? (
        <EmptyState
          icon={<PhoneIcon size={24} />}
          title={filter === "missed" ? "No missed calls" : "No calls yet"}
          className="flex-1"
        >
          {filter === "missed"
            ? "Calls you miss show up here."
            : "Start a call from any chat. Calls are end-to-end encrypted."}
        </EmptyState>
      ) : (
        <ul className="m-0 min-h-0 flex-1 list-none overflow-y-auto p-0 pb-4">
          {entries.map(({ call, direction }) => {
            const group = isGroupCall(state, call.chat_id);
            const title = callTitle(state, call);
            return (
              <li key={call.id}>
                <ListRow
                  leading={
                    <Avatar
                      name={title}
                      tone={toneFor(
                        group
                          ? call.chat_id
                          : (state.chats[call.chat_id]?.members.find((m) => m.user.id !== meId)?.user.id ??
                              call.chat_id),
                      )}
                      size={50}
                      shape={group ? "rounded" : "circle"}
                      icon={group ? <UsersIcon size={22} /> : undefined}
                    />
                  }
                  title={title}
                  titleClassName={direction === "missed" ? "text-scarlet" : undefined}
                  subtitle={
                    <>
                      <DirectionIcon direction={direction} />
                      <span className="truncate">{detailOf(call, direction, meId)}</span>
                    </>
                  }
                  trailing={
                    <IconButton
                      label={callBackLabel(state, call, direction)}
                      variant="soft"
                      shape="circle"
                      href={`/calls/${call.chat_id}?media=${call.media}`}
                    >
                      {call.media === "video" ? <VideoIcon size={19} /> : <PhoneIcon size={18} />}
                    </IconButton>
                  }
                />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
